// src/components/import/GridImportModal.tsx
// Import a class list (CSV) into an existing grid. Creating a brand-new grid is
// handled by the guided "Build a Grid" wizard (src/pages/grid/GridSetupWizard.tsx),
// so this modal focuses on one job: adding classes to a grid you already have,
// and — if the CSV carries a Line column — recording which line each class runs on.

import React, { useCallback, useState } from 'react';

import { Upload, FileText, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { parseCsvFile, ParsedSubjectList, ParsedMatrixData, SubjectData } from '../../utils/csvParser';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import { useToast } from '../../contexts/ToastContext';
import {
  buildClassesFromSubjects,
  buildLineSubjectMappings,
  saveLineAvailability,
} from '../../utils/gridSetup';
import { displayLearningArea } from '../../constants/learningAreas';

interface GridImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GridImportModal: React.FC<GridImportModalProps> = ({ isOpen, onClose }) => {
  const { allGrids, updateGrid } = useMultiGrid();
  const { addToast } = useToast();
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [selectedGridId, setSelectedGridId] = useState<string>('');
  const [previewData, setPreviewData] = useState<ParsedSubjectList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setStep('upload');
    setPreviewData(null);
    setSelectedGridId('');
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleFileUpload = useCallback(async (uploadedFile: File) => {
    try {
      setLoading(true);
      setError(null);
      const parsedData = await parseCsvFile(uploadedFile);

      // A matrix export (subject rows × line columns) is a whole-grid file, not a
      // class list. We no longer create grids from here — point the user at the wizard.
      if ('lineIds' in (parsedData as ParsedMatrixData)) {
        setError(
          'This looks like a full timetable matrix, not a class list. Use "Build a Grid" to create a grid, then import your class list here.'
        );
        return;
      }

      setPreviewData(parsedData as ParsedSubjectList);
      setStep('preview');
    } catch (err) {
      console.error('Error processing file:', err);
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0 && files[0].type === 'text/csv') {
        handleFileUpload(files[0]);
      } else {
        setError('Please upload a CSV file');
      }
    },
    [handleFileUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  // Add the parsed classes to the chosen grid, and — for any subject that carries
  // a Line — record its availability where the Timetable Editor reads it.
  const handleImport = async () => {
    if (!previewData) return;
    try {
      setLoading(true);

      if (!selectedGridId) {
        throw new Error('Please select a grid to import classes into.');
      }
      const targetGrid = allGrids.find((g) => g.id === selectedGridId);
      if (!targetGrid) {
        throw new Error('Selected grid not found.');
      }

      const validSubjects: SubjectData[] = previewData.subjects.filter(
        (s) => s.name?.trim() && s.code?.trim()
      );
      if (validSubjects.length === 0) {
        throw new Error(
          'No valid classes found in the CSV. Check the headers are Code, Name, Faculty (Line optional).'
        );
      }

      const newClasses = buildClassesFromSubjects(validSubjects, targetGrid, Date.now());
      await updateGrid(targetGrid.id, { classes: [...targetGrid.classes, ...newClasses] });

      const hasLineData = validSubjects.some((s) => s.line?.trim());
      if (hasLineData) {
        const mappings = buildLineSubjectMappings(validSubjects, targetGrid);
        try {
          await saveLineAvailability(targetGrid.id, mappings);
        } catch (err) {
          console.error('GridImport: Error saving line availability:', err);
          addToast('Classes added, but line availability could not be saved', 'warning');
        }
        addToast(
          `Imported ${validSubjects.length} classes and their line availability into ${targetGrid.name}`,
          'success'
        );
      } else {
        addToast(`Imported ${validSubjects.length} classes into ${targetGrid.name}`, 'success');
      }

      handleClose();
    } catch (err) {
      console.error('Error importing:', err);
      setError(err instanceof Error ? err.message : 'Failed to import');
    } finally {
      setLoading(false);
    }
  };

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div
        className="border-2 border-dashed border-ink-300 rounded-lg p-8 text-center hover:border-ink-400 transition-colors"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <FileText className="h-12 w-12 text-ink-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-ink-900 mb-2">
          Drag and drop a class-list CSV here, or click to select a file
        </p>
        <p className="text-sm text-ink-500 mb-4">
          Columns: Code, Name, Faculty (a Line column is optional)
        </p>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileInputChange}
          className="hidden"
          id="csv-upload"
        />
        <Button
          variant="secondary"
          onClick={() => document.getElementById('csv-upload')?.click()}
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Select CSV File'}
        </Button>
      </div>

      <div className="bg-navy-50 border border-navy-200 rounded-md p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-navy-500 mr-2 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-navy-800 mb-1">Class list format</h4>
            <ul className="text-sm text-navy-700 space-y-1 list-disc pl-5">
              <li>One class per row</li>
              <li>Columns: Code, Name, Faculty</li>
              <li>Optional Line column sets which line each class runs on</li>
              <li>Teachers are assigned afterwards in the Timetable Editor</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-danger-bg border border-danger-border rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-danger-fg mr-2 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-danger-fg">Couldn't use this file</h4>
              <p className="text-sm text-danger-fg mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => {
    if (!previewData) return null;
    const withLine = previewData.subjects.filter((s) => s.line?.trim()).length;
    return (
      <div className="space-y-4">
        <div className="bg-ok-bg border border-ok-border rounded-md p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-ok-fg mr-2 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-ok-fg">File processed successfully</h4>
              <p className="text-sm text-ok-fg mt-1">
                Found {previewData.totalSubjects} classes
                {withLine > 0 ? ` (${withLine} with a line assignment)` : ''}.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-navy-200 bg-navy-50 rounded-md p-4">
          <p className="text-sm text-navy-700 mb-3">Add these classes to which grid?</p>
          <select
            value={selectedGridId}
            onChange={(e) => setSelectedGridId(e.target.value)}
            className="w-full border border-ink-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500 mb-3"
          >
            <option value="">Choose a grid...</option>
            {allGrids.map((grid) => (
              <option key={grid.id} value={grid.id}>
                {grid.name} ({grid.classes.length} classes, {grid.defaultClassMinutes}min each)
              </option>
            ))}
          </select>
          {selectedGridId && (
            <p className="text-sm text-navy-600">
              Classes will be added to{' '}
              <strong>{allGrids.find((g) => g.id === selectedGridId)?.name}</strong>. Assign
              teachers afterwards in the Timetable Editor.
            </p>
          )}
        </div>

        <div className="border rounded-lg p-4">
          <h4 className="font-medium text-ink-900 mb-3">Sample classes</h4>
          <div className="text-xs text-ink-500 space-y-1 max-h-32 overflow-y-auto">
            {previewData.subjects.slice(0, 10).map((subject, index) => (
              <div key={index} className="flex gap-2">
                <span className="font-mono text-navy-600 flex-shrink-0">{subject.code}</span>
                <span className="flex-1 truncate">{subject.name}</span>
                <span className="text-ok-fg text-xs px-1 bg-ok-bg rounded">
                  {displayLearningArea(subject.faculty)}
                </span>
                {subject.line?.trim() && (
                  <span className="text-navy-700 text-xs px-1 bg-navy-100 rounded font-mono">
                    Line {subject.line.trim()}
                  </span>
                )}
              </div>
            ))}
            {previewData.subjects.length > 10 && (
              <div className="text-ink-500">
                ... and {previewData.subjects.length - 10} more classes
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-danger-bg border border-danger-border rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-danger-fg mr-2 flex-shrink-0" />
              <p className="text-sm text-danger-fg">{error}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getFooterButtons = () => {
    if (step === 'upload') {
      return (
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
      );
    }
    return (
      <>
        <Button variant="secondary" onClick={() => setStep('upload')}>
          Back
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={!selectedGridId || loading}>
          {loading ? 'Importing...' : 'Add classes to grid'}
        </Button>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import a class list"
      size="lg"
      footer={getFooterButtons()}
    >
      <div className="min-h-[400px]">
        {step === 'upload' && allGrids.length === 0 && (
          <div className="mb-4 bg-warn-bg border border-warn-border rounded-md p-4">
            <div className="flex">
              <Info className="h-5 w-5 text-warn-fg mr-2 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-warn-fg">No grids yet</h4>
                <p className="text-sm text-warn-fg mt-1">
                  Create a grid first with "Build a Grid", then come back here to import a class
                  list.
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 'upload' && renderUploadStep()}
        {step === 'preview' && renderPreviewStep()}
      </div>
    </Modal>
  );
};

// Export both named and default
export default GridImportModal;
